if (!customElements.get('cart-shipping-progress')) {
  customElements.define(
    'cart-shipping-progress',
    class CartShippingProgress extends HTMLElement {
      cartUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          const totalPrice = event.cartData?.total_price;
          const itemCount = event.cartData?.item_count;
          if (typeof totalPrice === 'number') {
            this.update(totalPrice, itemCount);
          }
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      update(totalCents, itemCount) {
        if (itemCount === 0) {
          this.classList.add('hidden');
          return;
        }
        this.classList.remove('hidden');
        this.dataset.totalCents = totalCents;

        if (this.dataset.mode === 'tiers') {
          this.updateTiers(totalCents);
        } else {
          this.updateSingle(totalCents);
        }
      }

      /* ── Single free-shipping milestone ─────────────────────────────────── */
      updateSingle(totalCents) {
        const thresholdCents = parseInt(this.dataset.thresholdCents, 10) || 0;
        const isUnlocked = thresholdCents <= 0 || totalCents >= thresholdCents;
        const progressPercent =
          thresholdCents <= 0 ? 100 : Math.min(Math.round((totalCents / thresholdCents) * 100), 100);
        const remainingCents = thresholdCents <= 0 ? 0 : Math.max(thresholdCents - totalCents, 0);

        this.dataset.unlocked = isUnlocked ? 'true' : 'false';
        this.dataset.milestoneState = isUnlocked ? 'completed' : progressPercent > 0 ? 'active' : 'upcoming';

        this.setFill(progressPercent);

        const milestone = this.querySelector('[data-shipping-progress-milestone]');
        this.setMilestoneState(milestone, isUnlocked ? 'completed' : progressPercent > 0 ? 'active' : 'upcoming');

        if (isUnlocked) {
          this.renderMessage(true);
        } else {
          this.renderMessage(false, remainingCents, this.dataset.rewardLabel || '');
        }
      }

      /* ── Multi-tier milestones ──────────────────────────────────────────── */
      updateTiers(totalCents) {
        const milestones = Array.from(this.querySelectorAll('[data-shipping-progress-milestone]'));
        if (!milestones.length) return;

        const thresholds = milestones.map((el) => parseInt(el.dataset.thresholdCents, 10) || 0);
        const maxThreshold = Math.max(...thresholds);
        const fillPercent = maxThreshold <= 0 ? 100 : Math.min(Math.round((totalCents / maxThreshold) * 100), 100);

        this.setFill(fillPercent);

        // Determine the next unreached tier (smallest threshold above current total)
        let nextThreshold = 0;
        let nextLabel = '';
        milestones.forEach((el) => {
          const th = parseInt(el.dataset.thresholdCents, 10) || 0;
          if (th > totalCents && (nextThreshold === 0 || th < nextThreshold)) {
            nextThreshold = th;
            nextLabel = el.dataset.label || '';
          }
        });

        const allCompleted = nextThreshold === 0;
        this.dataset.unlocked = allCompleted ? 'true' : 'false';

        // Per-milestone state
        milestones.forEach((el) => {
          const th = parseInt(el.dataset.thresholdCents, 10) || 0;
          let state = 'upcoming';
          if (totalCents >= th) state = 'completed';
          else if (th === nextThreshold) state = 'active';
          this.setMilestoneState(el, state);
        });

        if (allCompleted) {
          this.renderMessage(true);
        } else {
          this.renderMessage(false, Math.max(nextThreshold - totalCents, 0), nextLabel);
        }
      }

      /* ── Helpers ────────────────────────────────────────────────────────── */
      setFill(percent) {
        const fill = this.querySelector('[data-shipping-progress-fill]');
        if (fill) fill.style.width = `${percent}%`;

        const srLabel = this.querySelector('[data-shipping-progress-label]');
        if (srLabel) srLabel.textContent = `${percent}%`;

        const bar = this.querySelector('[role="progressbar"]');
        if (bar) bar.setAttribute('aria-valuenow', percent);
      }

      setMilestoneState(el, state) {
        if (!el) return;
        el.classList.remove(
          'cart-shipping-progress__milestone--upcoming',
          'cart-shipping-progress__milestone--active',
          'cart-shipping-progress__milestone--completed'
        );
        el.classList.add(`cart-shipping-progress__milestone--${state}`);
      }

      renderMessage(isUnlocked, remainingCents, rewardLabel) {
        const message = this.querySelector('[data-shipping-progress-text]');
        if (!message) return;

        message.replaceChildren();

        if (isUnlocked) {
          message.textContent = this.dataset.successMessage;
          return;
        }

        const remainingFormatted = this.formatMoney(remainingCents, this.dataset.moneyFormat);
        const template = (this.dataset.remainingMessage || '').replace('[reward]', rewardLabel || '');

        if (template.includes('[amount]')) {
          const parts = template.split('[amount]');
          if (parts[0]) message.append(parts[0]);

          const strong = document.createElement('strong');
          strong.textContent = remainingFormatted;
          message.append(strong);

          if (parts[1]) message.append(parts[1]);
        } else {
          message.textContent = template.replace('[amount]', remainingFormatted);
        }
      }

      formatMoney(cents, format) {
        if (typeof cents === 'string') cents = cents.replace('.', '');

        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const formatString = format || '${{amount}}';
        const match = formatString.match(placeholderRegex);

        if (!match) return (cents / 100).toFixed(2);

        const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
          if (isNaN(number) || number == null) return '0';
          number = (number / 100.0).toFixed(precision);
          const parts = number.split('.');
          const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          const centsStr = parts[1] ? decimal + parts[1] : '';
          return dollars + centsStr;
        };

        let value = '';
        switch (match[1]) {
          case 'amount':                                   value = formatWithDelimiters(cents, 2); break;
          case 'amount_no_decimals':                       value = formatWithDelimiters(cents, 0); break;
          case 'amount_with_comma_separator':              value = formatWithDelimiters(cents, 2, '.', ','); break;
          case 'amount_no_decimals_with_comma_separator':  value = formatWithDelimiters(cents, 0, '.', ','); break;
          case 'amount_with_apostrophe_separator':         value = formatWithDelimiters(cents, 2, "'", '.'); break;
          case 'amount_no_decimals_with_space_separator':  value = formatWithDelimiters(cents, 0, ' '); break;
          case 'amount_with_space_separator':              value = formatWithDelimiters(cents, 2, ' ', ','); break;
          default:                                         value = formatWithDelimiters(cents, 2);
        }

        return formatString.replace(placeholderRegex, value);
      }
    }
  );
}
